import React, { useRef, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaTimes,
  FaCog,
  FaSyncAlt,
  FaCamera,
  FaCameraRetro,
  FaImages,
  FaPhotoVideo,
  FaChevronLeft,
  FaChevronRight,
  FaRedo,
} from 'react-icons/fa';
import { useAuth } from './context/AuthContext';
import { supabase } from './supabaseClient';
import { uploadImage } from './services/uploadService';
import {
  savePendingUpload,
  deletePendingUpload,
  getPendingUploadsForEvent,
} from './services/pendingUploadStore';
import { joinEventAsGuest, setGuestDisplayName } from './services/eventAccessService';
import { applyVideoFilters, applyCanvasFilters, FILTER_ORDER } from './components/CameraFilters';
import { TimerCountdownOverlay } from './components/CameraTimer';
import { CameraSettingsSheet } from './components/CameraSettingsSheet';
import { GuestNamePrompt } from './components/GuestNamePrompt';
import { Spinner } from './components/Spinner';
import './CameraCapture.css';

const FILTER_LABELS = { normal: 'Normal', bw: 'B&W', sepia: 'Sepia' };
const GUEST_NAME_STORAGE_KEY = 'capture_guest_name';
// Generous enough for a large photo over a genuinely slow-but-working
// connection; a fully stalled request otherwise never rejects on its own.
const UPLOAD_TIMEOUT_MS = 60000;

function CameraCapture() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  const navigate = useNavigate();
  const { user, signInAsGuest } = useAuth();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const nativeCameraInputRef = useRef(null);

  const [facingMode, setFacingMode] = useState('user');
  const [eventName, setEventName] = useState('');
  const [guestCount, setGuestCount] = useState(null);
  const [photoCap, setPhotoCap] = useState(null);
  const [myUploadCount, setMyUploadCount] = useState(0);
  // Each item: { id, previewUrl, file, status: 'pending' | 'uploading' | 'failed' }
  // Items are removed as soon as they upload successfully — this list is
  // only ever "things still in flight or that need attention."
  const [pendingUploads, setPendingUploads] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [displayName, setDisplayName] = useState(null);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [cachedName] = useState(() => {
    try {
      return localStorage.getItem(GUEST_NAME_STORAGE_KEY) || '';
    } catch (err) {
      return '';
    }
  });

  // Filter/timer states
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [filter, setFilter] = useState('normal');
  const [showGrid, setShowGrid] = useState(false);
  const [armedSeconds, setArmedSeconds] = useState(null);
  const [countdown, setCountdown] = useState(null);

  const refreshGuestCount = React.useCallback(async () => {
    if (!eventId) return;
    const { data, error } = await supabase.rpc('get_event_guest_count', {
      p_event_id: eventId,
    });
    if (!error && typeof data === 'number') setGuestCount(data);
  }, [eventId]);

  // Tracks the per-guest shot quota (a competitive, POV-style scarcity
  // mechanic on lower tiers) so the shutter can show/enforce it live.
  const refreshMyUploadCount = React.useCallback(async () => {
    if (!eventId || !user) return;
    const { count, error } = await supabase
      .from('photos')
      .select('id', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .eq('uploaded_by', user.id);
    if (!error && typeof count === 'number') setMyUploadCount(count);
  }, [eventId, user]);

  useEffect(() => {
    if (!eventId || !user) return;

    const fetchPhotoCap = async () => {
      // A guest may not have an event_access row yet at this point (join
      // happens in a separate effect), so this reads through the
      // SECURITY DEFINER get_public_event_info() rather than the events
      // table directly — see fix-events-rls-leak.sql.
      const { data, error } = await supabase
        .rpc('get_public_event_info', { p_event_id: eventId })
        .single();
      if (!error) setPhotoCap(data?.photo_cap_per_guest ?? null);
    };

    fetchPhotoCap();
    refreshMyUploadCount();
  }, [eventId, user, refreshMyUploadCount]);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) return;

      try {
        // Same reasoning as fetchPhotoCap above — reads through the
        // public RPC since a brand-new guest has no event_access row yet.
        const { data, error } = await supabase
          .rpc('get_public_event_info', { p_event_id: eventId })
          .single();

        if (error) throw error;
        if (data) setEventName(data.name);
      } catch (err) {
        toast.error('Event not found');
        console.error('Error:', err);
      }
    };

    fetchEvent();
    refreshGuestCount();
  }, [eventId, refreshGuestCount]);

  useEffect(() => {
    if (!eventId) return;

    let cancelled = false;
    setJoining(true);
    setJoinError(null);

    joinEventAsGuest(eventId, signInAsGuest)
      .then(({ displayName: savedName }) => {
        if (cancelled) return;
        if (savedName) {
          setDisplayName(savedName);
        } else {
          setShowNamePrompt(true);
        }
      })
      .catch((err) => {
        console.error('Error joining event:', err);
        if (!cancelled) {
          setJoinError(err.message || 'Could not join this event');
          toast.error(err.message || 'Could not join this event');
        }
      })
      .finally(() => {
        if (!cancelled) setJoining(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const handleNameSubmit = async (name) => {
    setShowNamePrompt(false);
    setDisplayName(name);
    try {
      localStorage.setItem(GUEST_NAME_STORAGE_KEY, name);
    } catch (err) {
      // Private browsing / storage disabled — fine, it just won't be
      // pre-filled next time.
    }

    if (user) {
      try {
        await setGuestDisplayName(eventId, user.id, name);
      } catch (err) {
        toast.error("Couldn't save your name, but you can still upload");
      }
    }
  };

  const handleNameSkip = () => {
    setShowNamePrompt(false);
    setDisplayName('Guest');
  };

  const startCamera = React.useCallback(async () => {
    try {
      // Without explicit width/height constraints, browsers often default
      // to a modest video resolution — far below what the phone's camera
      // can actually do — so every photo starts life already soft before
      // any compression happens. Ask for the highest resolution available;
      // the browser negotiates down to whatever the device truly supports.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 3840 },
          height: { ideal: 2160 },
        },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast.error('Could not access camera');
      console.error('Camera error:', err);
    }
  }, [facingMode]);

  useEffect(() => {
    startCamera();
    const video = videoRef.current;
    return () => {
      if (video?.srcObject) {
        video.srcObject.getTracks().forEach((track) => track.stop());
      }
    };
  }, [startCamera]);

  // Apply filters to video feed
  useEffect(() => {
    if (videoRef.current) {
      applyVideoFilters(videoRef.current, brightness, contrast, filter);
    }
  }, [brightness, contrast, filter]);

  // Uploads a single item in the background the moment it's captured/picked
  // — guests never have to remember to hit a separate "upload" button.
  const uploadItem = React.useCallback(
    async (item) => {
      if (joinError) {
        // Never got an event_access row, so this would only fail RLS —
        // surfacing that raw error is more confusing than the blocking
        // join-error screen the guest is already looking at.
        setPendingUploads((prev) =>
          prev.map((p) => (p.id === item.id ? { ...p, status: 'failed' } : p))
        );
        return;
      }

      if (!eventId || !user) {
        setPendingUploads((prev) =>
          prev.map((p) => (p.id === item.id ? { ...p, status: 'pending' } : p))
        );
        return;
      }

      setPendingUploads((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, status: 'uploading' } : p))
      );

      try {
        // A dropped/stalled connection doesn't necessarily reject — it can
        // just hang, since fetch has no built-in timeout. Without this, a
        // stalled upload would sit on "uploading" forever: not 'failed', so
        // the auto-retry effect above would never pick it up either.
        const result = await Promise.race([
          uploadImage(item.file, eventId, user.id, null, displayName),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error('Upload timed out — check your connection')),
              UPLOAD_TIMEOUT_MS
            )
          ),
        ]);

        if (result.success) {
          setPendingUploads((prev) => prev.filter((p) => p.id !== item.id));
          setMyUploadCount((prev) => prev + 1);
          refreshGuestCount();
          deletePendingUpload(item.id);
        } else {
          toast.error(`Upload failed: ${result.error}`);
          setPendingUploads((prev) =>
            prev.map((p) => (p.id === item.id ? { ...p, status: 'failed' } : p))
          );
        }
      } catch (error) {
        toast.error(`Upload failed: ${error.message}`);
        setPendingUploads((prev) =>
          prev.map((p) => (p.id === item.id ? { ...p, status: 'failed' } : p))
        );
      }
    },
    [eventId, user, refreshGuestCount, displayName, joinError]
  );

  // Flushes any photos captured before the guest session finished joining.
  useEffect(() => {
    if (joining || !user) return;
    pendingUploads
      .filter((item) => item.status === 'pending')
      .forEach((item) => uploadItem(item));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joining, user]);

  // Resumes anything that never finished uploading last time this event's
  // camera was open on this device — the tab got closed, crashed, or lost
  // network mid-upload. Runs once join settles (same gating as the flush
  // effect above), independently of it, since these items don't exist in
  // React state yet when that effect runs.
  useEffect(() => {
    if (!eventId || joining || !user) return;
    let cancelled = false;

    getPendingUploadsForEvent(eventId).then((stored) => {
      if (cancelled || stored.length === 0) return;

      const recovered = stored.map((s) => ({
        id: s.id,
        previewUrl: URL.createObjectURL(s.file),
        file: s.file,
        status: 'pending',
      }));

      setPendingUploads((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        return [...prev, ...recovered.filter((r) => !existingIds.has(r.id))];
      });
      toast.info(
        `Resuming ${recovered.length} photo${recovered.length > 1 ? 's' : ''} that didn't finish uploading last time…`
      );
      recovered.forEach((item) => uploadItem(item));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, joining, user]);

  // Retries anything still marked failed whenever the tab comes back to
  // the foreground or the network comes back — covers backgrounding the
  // app, switching to the native camera and back, walking through a dead
  // spot, etc., without the guest having to notice and tap Retry manually.
  useEffect(() => {
    const failedItems = pendingUploads.filter((p) => p.status === 'failed');
    if (failedItems.length === 0) return;

    const retryAll = () => failedItems.forEach((item) => uploadItem(item));
    const onVisibility = () => {
      if (document.visibilityState === 'visible') retryAll();
    };

    window.addEventListener('online', retryAll);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('online', retryAll);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pendingUploads, uploadItem]);

  // Chrome/Firefox show a native "leave site?" confirmation on this;
  // Safari on iOS ignores it, and no browser fires it for backgrounding
  // (switching apps, locking the screen) — only an actual tab close/reload.
  // Best-effort, not a guarantee, which is exactly why the recovery effect
  // above exists as the real safety net.
  useEffect(() => {
    if (pendingUploads.length === 0) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [pendingUploads.length]);

  const captureImage = async () => {
    if (photoCap != null && myUploadCount >= photoCap) {
      toast.error(`You've used all ${photoCap} of your photos for this event.`);
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Apply filters to captured image
    let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    applyCanvasFilters(canvas, ctx, imageData, brightness, contrast, filter);

    // JPEG, not PNG: a lossless PNG of a busy 4K scene would be enormous
    // for no visible benefit over JPEG at quality 1 (max — no re-encoding
    // loss beyond what canvas's JPEG encoder inherently applies).
    const imageDataUrl = canvas.toDataURL('image/jpeg', 1);
    const blob = await fetch(imageDataUrl).then((res) => res.blob());
    const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
    const item = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      previewUrl: imageDataUrl,
      file,
      status: 'pending',
    };

    setPendingUploads((prev) => {
      const next = [...prev, item];
      setCurrentIndex(next.length - 1);
      return next;
    });
    savePendingUpload({ id: item.id, eventId, file, createdAt: Date.now() });
    uploadItem(item);
  };

  const handleShutterPress = () => {
    if (photoCap != null && myUploadCount >= photoCap) {
      toast.error(`You've used all ${photoCap} of your photos for this event.`);
      return;
    }

    if (armedSeconds) {
      setCountdown(armedSeconds);
    } else {
      captureImage();
    }
  };

  // Countdown ticks down once armed, capturing on reaching zero.
  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      captureImage();
      setCountdown(null);
      return;
    }

    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  const cycleFilter = () => {
    const currentIdx = FILTER_ORDER.indexOf(filter);
    setFilter(FILTER_ORDER[(currentIdx + 1) % FILTER_ORDER.length]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (photoCap != null) {
      const remaining = Math.max(0, photoCap - myUploadCount);
      if (remaining === 0) {
        toast.error(`You've used all ${photoCap} of your photos for this event.`);
        e.target.value = '';
        return;
      }
      if (files.length > remaining) {
        toast.warning(`Only ${remaining} more photo(s) allowed. Uploading the first ${remaining}.`);
        files.length = remaining;
      }
    }

    const newItems = files.map((file, i) => ({
      id: `${Date.now()}-${i}`,
      previewUrl: URL.createObjectURL(file),
      file,
      status: 'pending',
    }));

    setPendingUploads((prev) => [...prev, ...newItems]);
    setShowGallery(true);
    e.target.value = '';

    // One at a time, not all at once: normalizeImage's canvas re-encode
    // (imageValidation.js — HEIC/HEIF only, but a multi-select batch can
    // easily be several of those at once) is what pushes mobile browsers
    // into the memory pressure that makes canvas.toBlob() silently hand
    // back an empty result — previously surfaced as a confusing "No
    // content provided" error from Supabase Storage for every photo in
    // the batch. savePendingUpload runs first for each item regardless of
    // upload order, so a closed/crashed tab can still recover all of them.
    (async () => {
      for (const item of newItems) {
        savePendingUpload({ id: item.id, eventId, file: item.file, createdAt: Date.now() });
        await uploadItem(item);
      }
    })();
  };

  const retryItem = (item) => uploadItem(item);

  const discardItem = (index) => {
    setPendingUploads((prev) => {
      const discarded = prev[index];
      if (discarded) deletePendingUpload(discarded.id);
      return prev.filter((_, i) => i !== index);
    });
    if (currentIndex >= pendingUploads.length - 1) {
      setCurrentIndex(Math.max(0, currentIndex - 1));
    }
  };

  const flipCamera = () => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  if (!eventId) {
    return (
      <div className="camera-fullscreen">
        <div className="no-event-message">
          <p>No event selected</p>
          <button onClick={() => navigate('/')}>Go Back</button>
        </div>
      </div>
    );
  }

  if (joinError) {
    return (
      <div className="camera-fullscreen">
        <div className="no-event-message">
          <p>{joinError}</p>
          <button onClick={() => navigate(`/gallery?event=${eventId}`)}>
            View Gallery Instead
          </button>
        </div>
      </div>
    );
  }

  const failedCount = pendingUploads.filter((i) => i.status === 'failed').length;
  const currentItem = pendingUploads[currentIndex];
  const remainingShots = photoCap == null ? null : Math.max(0, photoCap - myUploadCount);
  const outOfShots = remainingShots === 0;

  return (
    <div className="camera-fullscreen">
      <div className="top-bar">
        <button
          className="icon-button"
          onClick={() => navigate(`/gallery?event=${eventId}`)}
          title="Close"
        >
          <FaTimes />
        </button>
        <div className="event-name-block">
          <span className="event-name">{eventName || 'Camera'}</span>
          {(guestCount !== null || remainingShots !== null) && (
            <span className="event-participants">
              {guestCount !== null && `${guestCount} ${guestCount === 1 ? 'guest' : 'guests'}`}
              {guestCount !== null && remainingShots !== null && ' · '}
              {remainingShots !== null && `${remainingShots} shots left`}
            </span>
          )}
        </div>
        <button
          className="icon-button"
          onClick={() => setShowSettings(true)}
          title="Camera settings"
        >
          <FaCog />
        </button>
      </div>

      <video ref={videoRef} autoPlay playsInline className="video-feed" />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {showGrid && <div className="grid-overlay" />}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/*
        A plain file input with `capture` hands off to the phone's actual
        native camera app instead of this page's WebRTC viewfinder — no
        browser video/canvas re-encoding in the path, so it's the highest
        quality a guest's phone can produce. Trade-off: no live filter
        preview, grid, or timer, since that's a modal OS handoff rather
        than something we can draw over. Points at whichever camera is
        currently selected so it stays in sync with the flip button.
      */}
      <input
        ref={nativeCameraInputRef}
        type="file"
        accept="image/*"
        capture={facingMode === 'user' ? 'user' : 'environment'}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      <CameraSettingsSheet
        open={showSettings}
        onClose={() => setShowSettings(false)}
        brightness={brightness}
        contrast={contrast}
        filter={filter}
        showGrid={showGrid}
        onBrightnessChange={setBrightness}
        onContrastChange={setContrast}
        onFilterChange={setFilter}
        onGridToggle={() => setShowGrid(!showGrid)}
        armedSeconds={armedSeconds}
        onArmTimer={setArmedSeconds}
      />

      <TimerCountdownOverlay countdown={countdown} onCancel={() => setCountdown(null)} />

      <GuestNamePrompt
        open={showNamePrompt}
        defaultValue={cachedName}
        onSubmit={handleNameSubmit}
        onSkip={handleNameSkip}
      />

      {showGallery && currentItem && (
        <div className="preview-overlay">
          <div className="preview-header">
            <button
              className="close-btn"
              onClick={() => setShowGallery(false)}
              title="Close"
            >
              ✕
            </button>
            <span className="preview-counter">
              {currentIndex + 1} / {pendingUploads.length}
            </span>
            <button
              className="delete-btn"
              onClick={() => discardItem(currentIndex)}
              title="Discard"
            >
              🗑️
            </button>
          </div>

          <img
            src={currentItem.previewUrl}
            alt="Captured"
            className="preview-image"
          />

          <div className="upload-status">
            {currentItem.status === 'uploading' && (
              <span className="upload-status-inline">
                <Spinner size="small" variant="light" /> Uploading…
              </span>
            )}
            {currentItem.status === 'pending' && <span>Waiting to upload…</span>}
            {currentItem.status === 'failed' && (
              <button className="retry-btn" onClick={() => retryItem(currentItem)}>
                <FaRedo /> Upload failed. Retry
              </button>
            )}
          </div>

          <div className="gallery-controls">
            <button
              onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              title="Previous"
            >
              <FaChevronLeft />
            </button>
            <button
              onClick={() =>
                setCurrentIndex(Math.min(pendingUploads.length - 1, currentIndex + 1))
              }
              disabled={currentIndex === pendingUploads.length - 1}
              title="Next"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      <div className="shutter-row">
        <button
          className="native-camera-button"
          onClick={() => nativeCameraInputRef.current?.click()}
          title={outOfShots ? "You've used all your shots for this event" : "Use your phone's native camera (highest quality, no filters)"}
          disabled={outOfShots}
        >
          <FaCameraRetro />
        </button>

        <button className="filter-pill" onClick={cycleFilter} title="Cycle filter">
          {FILTER_LABELS[filter]}
        </button>

        <button
          className="shutter-button"
          onClick={handleShutterPress}
          title={outOfShots ? "You've used all your shots for this event" : 'Capture'}
          disabled={outOfShots}
        >
          <FaCamera />
        </button>

        <button className="flip-button" onClick={flipCamera} title="Flip camera">
          <FaSyncAlt />
        </button>
      </div>

      <div className="bottom-info-bar">
        <button
          className={`info-pill ${failedCount > 0 ? 'info-pill-alert' : ''}`}
          onClick={() =>
            pendingUploads.length > 0 ? setShowGallery(true) : fileInputRef.current?.click()
          }
          title={
            pendingUploads.length > 0
              ? 'Review uploads'
              : outOfShots
              ? "You've used all your shots for this event"
              : 'Choose from camera roll'
          }
          disabled={pendingUploads.length === 0 && outOfShots}
        >
          {pendingUploads.length > 0 ? (
            <>
              <span className="info-pill-stack">
                {pendingUploads.slice(-3).map((item, i, arr) => {
                  // depth 0 = most recent (front, sharp); higher depth =
                  // older photos peeking out from behind, fanned to the
                  // upper-left like a stack of physical prints.
                  const depth = arr.length - 1 - i;
                  return (
                    <img
                      key={item.id}
                      src={item.previewUrl}
                      alt=""
                      className="info-pill-stack-photo"
                      style={{
                        transform: `translate(${depth * -5}px, ${depth * -4}px) rotate(${depth * -7}deg)`,
                        zIndex: arr.length - depth,
                      }}
                    />
                  );
                })}
              </span>
              {failedCount > 0
                ? `${failedCount} failed. Tap to retry`
                : `Uploading ${pendingUploads.length}…`}
            </>
          ) : outOfShots ? (
            'Out of shots'
          ) : (
            <>
              <FaImages /> Camera roll
            </>
          )}
        </button>

        <button
          className="info-pill"
          onClick={() => navigate(`/gallery?event=${eventId}`)}
          title="View shared gallery"
        >
          <FaPhotoVideo /> Gallery
        </button>
      </div>
    </div>
  );
}

export default CameraCapture;
